import React, { useState, useEffect, useContext } from "react";
import { Box, SxProps, Theme, Button, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router";
import EstateFormsInfoCard, {
  EstateFormsInfoCardProps,
} from "../../shared/forms/EstateFormsInfoCard";
import { GeographyWatercolorImg } from "../../shared/GeographyWatercolorImg";
import MuiMarkdown from "mui-markdown";
import { FormsList } from "../../shared/forms/list/FormsList";
import { useAuthContext } from "AuthContextWrapper"
import { EstateFormsContext } from "EstateFormsContextWrapper";
import { State } from "models/Geography";
import { GeographyType } from "models/Geography";

interface Props {
  truncateFormsAt?: number;
  sx: SxProps<Theme>;
  openDrawer: () => void;
  setDrawerContents: (contents: JSX.Element) => void;
  onEmptyButtonClick?: () => void;
  goToProbateDetermination?: () => void;
  hideHeader?: boolean;
}

const EstateFormsComponent = ({
  openDrawer,
  setDrawerContents,
  truncateFormsAt,
  sx,
  onEmptyButtonClick,
  goToProbateDetermination,
  hideHeader = false,
}: Props): JSX.Element => {
  const navigate = useNavigate();
  const { activeEstate, states } = useAuthContext()
  const { estateForms } = useContext(EstateFormsContext);
  const [probateState, setProbateState] = useState<State | undefined>();
  const [estateState, setEstateState] = useState<State | undefined>();

  useEffect(() => {
    if (states) {
      const probateState = states.find(
        (state) => state.id === activeEstate?.probateType?.stateId
      );
      const estateState = states.find(
        (state) => state.id === activeEstate?.stateId
      );
      setProbateState(probateState);
      setEstateState(estateState);
    }
  }, [activeEstate, states]);

  const getEstateFormsInfoCard = (): JSX.Element => {
    let props: EstateFormsInfoCardProps;
    const packetCard = {
      title: `${activeEstate?.probateType?.probateAtticusTier?.name || "Probate "} Packet for ${probateState?.name || " your state"}`,
      description: `${activeEstate?.probateType?.name}`,
      imageElement: (
        <GeographyWatercolorImg
          geographySlug={probateState?.slug}
          sx={{ maxHeight: "5.5rem" }}
        />
      ),
    };
    const determineProbateCard = {
      title: `Let's figure out what ${estateState?.name} forms you'll need`,
      description: `Before we can tell which forms you’ll need, we need a bit more information. Answer these estate questions so we can determine which type of probate is best for this estate.`,
      imageElement: (
        <GeographyWatercolorImg
          geographySlug={estateState?.slug}
          sx={{ maxHeight: "5.5rem" }}
        />
      ),
      onClick: goToProbateDetermination,
    };
    const fallbackText = {
      title: `Some helpful information about ${estateState?.name} Probate`,
      description: `Check out our Guide to ${estateState?.name} Probate to get a quick rundown of what the process looks like.`,
      imageElement: (
        <GeographyWatercolorImg
          geographySlug={estateState?.slug}
          sx={{ maxHeight: "5.5rem" }}
        />
      ),
      onClick: () => {
        setDrawerContents(
          <MuiMarkdown
            overrides={{
              ol: {
                component: "ol",
                props: {
                  style: { marginBottom: "0.5rem", padding: "10px 20px" },
                },
              },
            }}
          >
            {estateState?.content.probateFallbackContent}
          </MuiMarkdown>
        );
        openDrawer();
      },
    };
    const lookingForEstateForms = {
      title: `Looking for ${estateState?.name} Estate & Probate Forms?`,
      description: `Use our Forms Library to search, find, access, and use more than 3,500 local court forms across the United States and Canada.`,
      imageElement: (
        <GeographyWatercolorImg
          geographySlug={estateState?.slug}
          sx={{ maxHeight: "5.5rem" }}
        />
      ),
      onClick: () => {
        if (activeEstate?.stateId) {
          navigate(`forms-library`, {
            state: {
              geography_type: GeographyType.State,
              geography_id: activeEstate?.stateId,
            },
          });
        }
      },
    };

    /*
    probatedef    
    |  probateassigned     
    |  |  havetext
    |  |  |
    T  T  T  - show text
    T  T  F  - show unclickable probate assigned packet card
    T  F  T  - show get-probate 
    T  F  F  - show get-probate
    F  T  T  - (should never happen) show text
    F  T  F  - (should never happen) looking for estate forms?
    F  F  T  - show text
    F  F  F  - looking for estate forms?
    */
    const stateHasProbate = !!estateState?.content?.hasProbateDefinitions;
    const probateAssigned = !!activeEstate?.probateType;
    const hasText = !!estateState?.content.probateFallbackContent;
    if (stateHasProbate) {
      if (probateAssigned) {
        if (hasText) {
          props = fallbackText;
        } else {
          props = packetCard;
        }
      } else {
        props = determineProbateCard;
      }
    } else {
      if (probateAssigned) {
        if (hasText) {
          props = fallbackText;
        } else {
          props = lookingForEstateForms;
        }
      } else {
        if (hasText) {
          props = fallbackText;
        } else {
          props = lookingForEstateForms;
        }
      }
    }

    return (
      <EstateFormsInfoCard
        {...props}
        sx={{
          mb: 2,
        }}
        loading={!activeEstate || !estateState}
      />
    );
  };

  return (
    <Box sx={sx}>
      {!hideHeader && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5">{`Estate Forms`}</Typography>
          <Typography variant="body1">
            {`Here’s a running list of forms that need to be completed. As you or any other users connected to your estate add required forms, they will begin to appear here. Atticus may also add suggested forms, based on information provided or steps you take throughout the process.`}
          </Typography>
        </Box>
      )}

      {/* We need to use the packet for the below. */}
      {getEstateFormsInfoCard()}
      <FormsList
        forms={estateForms}
        estate={activeEstate}
        truncateFormsAt={truncateFormsAt}
        emptyState={
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 4,
              padding: 4,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: "800",
                width: "17rem",
                textAlign: "center",
              }}
            >
              No forms added yet. As you add forms, they will appear here.
            </Typography>
            {onEmptyButtonClick && (
              <Button variant="outlined" onClick={onEmptyButtonClick}>
                Add forms
              </Button>
            )}
          </Box>
        }
      />
      {truncateFormsAt && estateForms && estateForms.length > 0 && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Button
            href="/app/forms-and-files/estate-forms"
            variant="outlined"
            endIcon={<ArrowForwardIcon />}
          >
            {truncateFormsAt && estateForms.length > truncateFormsAt
              ? `View All ${estateForms.length} Estate Forms`
              : `View All Estate Forms`}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default EstateFormsComponent;
